import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence } from "motion/react";
import { motion } from "motion/react";
import {
  Eye, Plus, Trash2, Building2, Award, Check, X, Pencil, Tag,
  Save, Loader2, CheckCircle, Sparkles
} from "lucide-react";
import AvatarUpload from "../ui/AvatarUpload";
import { specializations as allSpecializations } from "../../data/experts";
import AiProfileAssistant from "../ui/AiProfileAssistant";

export default function ExpertProfileTab({
  user,
  profile,
  setProfile,
  avatarUrl,
  handleAvatarChange,
  handleSaveProfile,
  profileSaving,
  profileSaved
}) {
  const [editingCompanyIndex, setEditingCompanyIndex] = useState(null);
  const [editingAchievementIndex, setEditingAchievementIndex] = useState(null);
  const [editCompanyDraft, setEditCompanyDraft] = useState({ name: "", position: "", years: "" });
  const [editAchievementDraft, setEditAchievementDraft] = useState("");
  const [showAiAssistant, setShowAiAssistant] = useState(false);

  const safeSpecializations = Array.isArray(profile.specializations) ? profile.specializations : [];
  const safeCompanies = Array.isArray(profile.companies) ? profile.companies : [];
  const safeAchievements = Array.isArray(profile.achievements) ? profile.achievements : [];

  const handleAddCompany = () => {
    setProfile({
      ...profile,
      companies: [...safeCompanies, { name: "", position: "", years: "" }]
    });
    setEditCompanyDraft({ name: "", position: "", years: "" });
    setEditingCompanyIndex(safeCompanies.length);
    setEditingAchievementIndex(null);
  };

  const handleAddAchievement = () => {
    setProfile({
      ...profile,
      achievements: [...safeAchievements, ""]
    });
    setEditAchievementDraft("");
    setEditingAchievementIndex(safeAchievements.length);
    setEditingCompanyIndex(null);
  };

  return (
    <motion.div
      id="expert-profile-tab_root"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-medium text-white">Редактирование профиля</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAiAssistant(!showAiAssistant)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              showAiAssistant
                ? "border-primary-500 bg-primary-500/10 text-primary-400"
                : "border-neutral-700 text-neutral-300 hover:border-primary-500/50 hover:text-primary-400"
            }`}
          >
            <Sparkles className="w-4 h-4" />
            <span className="text-lg font-extralight">Заполнить с AI</span>
          </button>
          <Link
          to="/experts/preview"
          state={{ previewProfile: profile }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-700 text-neutral-300 hover:border-neutral-500 hover:text-white transition-colors"
        >
          <Eye className="w-4 h-4" />
          <span className="text-lg font-extralight">Превью</span>
          </Link>
        </div>
      </div>

      <AnimatePresence>
        {showAiAssistant && (
          <AiProfileAssistant
            profile={profile}
            setProfile={setProfile}
            onClose={() => setShowAiAssistant(false)}
          />
        )}
      </AnimatePresence>

      <BasicInfoSection
        user={user}
        profile={profile}
        setProfile={setProfile}
        avatarUrl={avatarUrl}
        handleAvatarChange={handleAvatarChange}
      />

      <CompaniesSection
        safeCompanies={safeCompanies}
        profile={profile}
        setProfile={setProfile}
        editingCompanyIndex={editingCompanyIndex}
        setEditingCompanyIndex={setEditingCompanyIndex}
        editCompanyDraft={editCompanyDraft}
        setEditCompanyDraft={setEditCompanyDraft}
        setEditingAchievementIndex={setEditingAchievementIndex}
        handleAddCompany={handleAddCompany}
      />

      <AchievementsSection
        safeAchievements={safeAchievements}
        profile={profile}
        setProfile={setProfile}
        editingAchievementIndex={editingAchievementIndex}
        setEditingAchievementIndex={setEditingAchievementIndex}
        editAchievementDraft={editAchievementDraft}
        setEditAchievementDraft={setEditAchievementDraft}
        setEditingCompanyIndex={setEditingCompanyIndex}
        handleAddAchievement={handleAddAchievement}
      />

      <SpecializationsSection
        safeSpecializations={safeSpecializations}
        profile={profile}
        setProfile={setProfile}
      />

      <div className="flex justify-end">
        <button
          onClick={handleSaveProfile}
          disabled={profileSaving}
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
        >
          {profileSaving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : profileSaved ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          {profileSaving ? "Сохранение..." : profileSaved ? "Сохранено!" : "Сохранить изменения"}
        </button>
      </div>
    </motion.div>
  );
}

function BasicInfoSection({ user, profile, setProfile, avatarUrl, handleAvatarChange }) {
  return (
    <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
      <h3 className="text-xl font-medium text-white mb-4">Основная информация</h3>
      <div className="space-y-4">
        <AvatarUpload
          userId={user?.id}
          currentAvatarUrl={avatarUrl}
          onAvatarChange={handleAvatarChange}
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-lg font-extralight text-neutral-400 mb-2">Имя</label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-lg font-extralight text-neutral-400 mb-2">Должность</label>
            <input
              type="text"
              value={profile.title}
              onChange={(e) => setProfile({ ...profile, title: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-lg font-extralight text-neutral-400 mb-2">О себе</label>
          <textarea
            value={profile.bio}
            onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
            rows={4}
            className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500 resize-none"
          />
        </div>
        <div>
          <label className="block text-lg font-extralight text-neutral-400 mb-2">Стоимость консультации (&#8381;/час)</label>
          <input
            type="text"
            inputMode="numeric"
            value={profile.hourlyRate === 0 ? "" : profile.hourlyRate}
            onChange={(e) => {
              const raw = e.target.value.replace(/\D/g, "");
              setProfile({ ...profile, hourlyRate: raw === "" ? 0 : Number(raw) });
            }}
            placeholder="0"
            className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
          />
        </div>
        <div>
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              className={`relative w-11 h-6 rounded-full transition-colors ${
                profile.available ? "bg-primary-600" : "bg-neutral-700"
              }`}
              onClick={() => setProfile({ ...profile, available: !profile.available })}
            >
              <div
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  profile.available ? "left-6" : "left-1"
                }`}
              />
            </div>
            <span className="text-lg font-extralight text-neutral-300">Доступен для консультаций</span>
          </label>
        </div>
      </div>
    </div>
  );
}

function CompaniesSection({
  safeCompanies, profile, setProfile,
  editingCompanyIndex, setEditingCompanyIndex,
  editCompanyDraft, setEditCompanyDraft,
  setEditingAchievementIndex, handleAddCompany
}) {
  return (
    <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-medium text-white">Места работы</h3>
        <button
          onClick={handleAddCompany}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span className="text-lg font-extralight">Добавить</span>
        </button>
      </div>
      <div className="space-y-4">
        {safeCompanies.map((company, index) => (
          <div key={index} className="p-4 rounded-xl bg-neutral-800/50">
            {editingCompanyIndex === index ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-primary-400" />
                  </div>
                  <span className="text-lg font-medium text-primary-400">Редактирование</span>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={editCompanyDraft.name}
                    onChange={(e) => setEditCompanyDraft({ ...editCompanyDraft, name: e.target.value })}
                    className="px-3 py-2 rounded-lg bg-neutral-700 border border-neutral-600 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                    placeholder="Компания"
                  />
                  <input
                    type="text"
                    value={editCompanyDraft.position}
                    onChange={(e) => setEditCompanyDraft({ ...editCompanyDraft, position: e.target.value })}
                    className="px-3 py-2 rounded-lg bg-neutral-700 border border-neutral-600 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                    placeholder="Должность"
                  />
                  <input
                    type="text"
                    value={editCompanyDraft.years}
                    onChange={(e) => setEditCompanyDraft({ ...editCompanyDraft, years: e.target.value })}
                    className="px-3 py-2 rounded-lg bg-neutral-700 border border-neutral-600 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                    placeholder="Годы"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setEditingCompanyIndex(null)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-neutral-700 text-neutral-400 hover:text-white text-lg font-extralight transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Отмена
                  </button>
                  <button
                    onClick={() => {
                      const updated = [...safeCompanies];
                      updated[index] = { ...editCompanyDraft };
                      setProfile({ ...profile, companies: updated });
                      setEditingCompanyIndex(null);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    Сохранить
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-neutral-700 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-neutral-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-medium text-white">{company.name}</p>
                  <div className="flex items-center gap-4">
                    <span className="text-lg font-extralight text-neutral-400">{company.position}</span>
                    <span className="text-lg font-extralight text-neutral-500">{company.years}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      setEditCompanyDraft({ name: company.name, position: company.position, years: company.years });
                      setEditingCompanyIndex(index);
                      setEditingAchievementIndex(null);
                    }}
                    className="p-2 rounded-lg hover:bg-primary-500/10 text-neutral-500 hover:text-primary-400 transition-colors"
                    title="Редактировать"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setProfile({ ...profile, companies: safeCompanies.filter((_, i) => i !== index) });
                      if (editingCompanyIndex === index) setEditingCompanyIndex(null);
                    }}
                    className="p-2 rounded-lg hover:bg-red-500/10 text-neutral-500 hover:text-red-400 transition-colors"
                    title="Удалить"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AchievementsSection({
  safeAchievements, profile, setProfile,
  editingAchievementIndex, setEditingAchievementIndex,
  editAchievementDraft, setEditAchievementDraft,
  setEditingCompanyIndex, handleAddAchievement
}) {
  return (
    <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-medium text-white">Достижения</h3>
        <button
          onClick={handleAddAchievement}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span className="text-lg font-extralight">Добавить</span>
        </button>
      </div>
      <div className="space-y-3">
        {safeAchievements.map((achievement, index) => (
          <div key={index} className="p-3 rounded-xl bg-neutral-800/50">
            {editingAchievementIndex === index ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary-400 flex-shrink-0" />
                  <span className="text-lg font-medium text-primary-400">Редактирование</span>
                </div>
                <input
                  type="text"
                  value={editAchievementDraft}
                  onChange={(e) => setEditAchievementDraft(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-neutral-700 border border-neutral-600 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                  placeholder="Описание достижения"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && editAchievementDraft.trim()) {
                      const updated = [...safeAchievements];
                      updated[index] = editAchievementDraft.trim();
                      setProfile({ ...profile, achievements: updated });
                      setEditingAchievementIndex(null);
                    }
                    if (e.key === "Escape") setEditingAchievementIndex(null);
                  }}
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setEditingAchievementIndex(null)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-neutral-700 text-neutral-400 hover:text-white text-lg font-extralight transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Отмена
                  </button>
                  <button
                    onClick={() => {
                      const updated = [...safeAchievements];
                      updated[index] = editAchievementDraft.trim();
                      setProfile({ ...profile, achievements: updated });
                      setEditingAchievementIndex(null);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    Сохранить
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Award className="w-5 h-5 text-accent-400 flex-shrink-0" />
                <span className="text-lg font-extralight text-neutral-300 flex-1">{achievement}</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      setEditAchievementDraft(achievement);
                      setEditingAchievementIndex(index);
                      setEditingCompanyIndex(null);
                    }}
                    className="p-2 rounded-lg hover:bg-primary-500/10 text-neutral-500 hover:text-primary-400 transition-colors"
                    title="Редактировать"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setProfile({ ...profile, achievements: safeAchievements.filter((_, i) => i !== index) });
                      if (editingAchievementIndex === index) setEditingAchievementIndex(null);
                    }}
                    className="p-2 rounded-lg hover:bg-red-500/10 text-neutral-500 hover:text-red-400 transition-colors"
                    title="Удалить"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SpecializationsSection({ safeSpecializations, profile, setProfile }) {
  return (
    <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
      <div className="flex items-center gap-3 mb-2">
        <Tag className="w-5 h-5 text-primary-400" />
        <h3 className="text-xl font-medium text-white">Тематики консультаций</h3>
      </div>
      <p className="text-lg font-extralight text-neutral-500 mb-4">
        Выберите темы, по которым вы готовы консультировать. Они будут отображаться на вашей карточке и в профиле.
      </p>
      <div className="flex flex-wrap gap-2">
        {allSpecializations.map((spec) => {
          const isSelected = safeSpecializations.includes(spec);
          return (
            <button
              key={spec}
              type="button"
              onClick={() => {
                const newSpecs = isSelected
                  ? safeSpecializations.filter((s) => s !== spec)
                  : [...safeSpecializations, spec];
                setProfile({ ...profile, specializations: newSpecs });
              }}
              className={`px-4 py-2 rounded-md text-lg font-extralight transition-all ${
                isSelected
                  ? "bg-primary-600/20 text-primary-300 border border-primary-500/40"
                  : "bg-neutral-800/50 text-neutral-500 border border-neutral-700/50 hover:border-neutral-600 hover:text-neutral-300"
              }`}
            >
              {spec}
            </button>
          );
        })}
      </div>
      {safeSpecializations.length > 0 && (
        <div className="mt-4 pt-4 border-t border-neutral-800/50">
          <p className="text-lg font-extralight text-neutral-500 mb-2">
            Выбрано: {safeSpecializations.length}
          </p>
          <div className="flex flex-wrap gap-2">
            {safeSpecializations.map((spec) => (
              <span
                key={spec}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary-500/10 text-primary-400 text-lg font-extralight"
              >
                {spec}
                <button
                  type="button"
                  onClick={() => {
                    setProfile({
                      ...profile,
                      specializations: safeSpecializations.filter((s) => s !== spec)
                    });
                  }}
                  className="hover:text-red-400 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
